import { html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { translateFn } from '../../../../utils/translator.helper';
import { ResourcesTypes, RiskManagementResourcesByOSPayload } from '@nirvana/shared/dtos/types/widgets/common';
import { ColumnKeys as DevicesColumnKeys } from '../../../devicesApp/types';
import { AffectedDeviceType, State } from '../../../common/constants';
import { OperatingSystemType } from '@nirvana/shared/dtos/types/protected-entities/common';
import { BaseWebComponentTranslator } from '../../../../framework/BaseWebComponentTranslator';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { AreaSeries } from '../../../../ui/gzGeneralChart/types';

/**
 * @summary Type here the description of this component
 * @tag gz-rm-resources-by-os-widget
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-rm-resources-by-os-widget')
export class GzRmResourcesByOsWidgetWebComponent extends GzGenericWidgetContent {
  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state: WidgetStateFrontEnd;

  @property({ type: Object, attribute: true })
  payload: RiskManagementResourcesByOSPayload;

  private categoriesOrder = [AffectedDeviceType.ENDPOINT, AffectedDeviceType.SERVER];

  private allowedOSTypesOrder = [OperatingSystemType.WINDOWS, OperatingSystemType.LINUX, OperatingSystemType.MACOSX];

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  protected renderLoader(): TemplateResult {
    return html`
      <gz-loader rows="1" columns="1" style="margin-top: calc(var(--spacer) * 8)"></gz-loader>
      <gz-loader rows="1" columns="1" style="margin-top: calc(var(--spacer) * 12); width: 50%;"></gz-loader>
    `;
  }

  protected renderContent(): TemplateResult {
    const categories = [translateFn('Workstations'), translateFn('Servers')];

    const seriesValues: AreaSeries[] = [];
    const seriesValuesMapping = {};

    for (const osType of this.allowedOSTypesOrder) {
      seriesValuesMapping[osType] = {
        name: BaseWebComponentTranslator.getInstance().getOperatingSystem(osType),
        data: [],
      };
    }

    this.payload?.resourcesByOS?.items?.forEach(item => {
      for (const categoryOrder of this.categoriesOrder) {
        const foundItem: ResourcesTypes | undefined = item.counts.find(endpointTypeData => {
          return endpointTypeData.endpointType === categoryOrder;
        });

          // If no item found, we should keep the order of the categories, so we will put empty value
          if (!foundItem) {
            seriesValuesMapping[item.operatingSystem]['data'].push(0);
            continue;
          }

          seriesValuesMapping[item.operatingSystem]['data'].push(foundItem.count);
      }
    });

    // Order the series values based on the allowedOSTypesOrder order
    for (const osType of this.allowedOSTypesOrder) {
      seriesValues.push(seriesValuesMapping[osType]);
    }

    return html`<gz-horizontal-stacked-bar-chart
      .series="${seriesValues}"
      ?emptyState="${this.state === WidgetStateFrontEnd.IS_EMPTY}"
      .categories="${categories}"
      .openNewTabOnLegendClicked="${this.openNewTabFromLegend.bind(this)}"
      .width="${this.contentWidth}"
      .height="${this.contentHeight}"
    >
    </gz-horizontal-stacked-bar-chart>`;
  }

  public handleTitleClick(): void {
    const filters = [
      {
        [DevicesColumnKeys.COMPANY]: { exactMatch: this.payload?.resourcesByOS?.companyId },
      },
      {
        [DevicesColumnKeys.STATE]: {
          in: [State.ACTIVE],
        },
      },
      {
        [DevicesColumnKeys.RISK_SCORE]: {
          range: {
            min: 1,
            max: 100,
          },
        },
      },
      {
        [DevicesColumnKeys.OS]: {
          in: [OperatingSystemType.WINDOWS, OperatingSystemType.LINUX, OperatingSystemType.MACOSX],
        },
      },
      {
        [DevicesColumnKeys.DEVICE_TYPE]: {
          in: [AffectedDeviceType.ENDPOINT, AffectedDeviceType.SERVER],
        },
      },
    ];

    NavigationHelper.navigateToRiskManagement({
      company: { id: this.payload?.resourcesByOS?.companyId ?? '' },
      section: 'resources',
      newTab: true,
      filters,
    });
  }

  openNewTabFromLegend(seriesIndex) {
    const operatingSystem = this.allowedOSTypesOrder[seriesIndex] ?? 0;

    const filters = [
      {
        [DevicesColumnKeys.COMPANY]: { exactMatch: this.payload?.resourcesByOS?.companyId },
      },
      {
        [DevicesColumnKeys.STATE]: {
          in: [State.ACTIVE],
        },
      },
      {
        [DevicesColumnKeys.RISK_SCORE]: {
          range: {
            min: 1,
            max: 100,
          },
        },
      },
      {
        [DevicesColumnKeys.OS]: {
          in: [operatingSystem],
        },
      },
      {
        [DevicesColumnKeys.DEVICE_TYPE]: {
          in: [AffectedDeviceType.ENDPOINT, AffectedDeviceType.SERVER],
        },
      },
    ];

    NavigationHelper.navigateToRiskManagement({
      company: { id: this.payload?.resourcesByOS?.companyId ?? '' },
      section: 'resources',
      newTab: true,
      filters,
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-rm-resources-by-os-widget': GzRmResourcesByOsWidgetWebComponent;
  }
}
